import { Placement } from '@popperjs/core'
import classnames from 'classnames'
import React from 'react'
import Spinner from '../Loaders/Spinner'
import Popover from '../Popover/Popover'

export enum BUTTON_TYPE {
  SOLID = 'solid',
  OUTLINE = 'outline',
  TEXT = 'text'
}

export enum BUTTON_SIZE {
  SMALL = 'small',
  MEDIUM = 'medium',
  LARGE = 'large'
}

export enum BUTTON_COLOR {
  PRIMARY = 'primary',
  DANGER = 'danger',
  SUCCESS = 'success'
}

export interface IButtonOption {
  name: string
  icon?: string
  disabled?: boolean
  onClick: () => void
}

export interface IButtonProps {
  children: React.ReactNode | undefined
  onClick?: any
  onClickCapture?: any
  style?: React.CSSProperties
  type?: BUTTON_TYPE
  size?: BUTTON_SIZE
  color?: BUTTON_COLOR
  colorClass?: string
  className?: string
  disabled?: boolean
  loading?: boolean
  leftIcon?: string
  rightIcon?: string
  htmlType?: 'button' | 'submit' | 'reset'
  additionalOptions?: IButtonOption[]
  additionalOptionsPopperPlacement?: Placement
  optionsButton?: boolean
  renderOptionsButtonArrow?: boolean
}

const theme = {
  shared:
    'inline-flex items-center content-center justify-center rounded-md whitespace-nowrap focus:outline-none disabled:hover:shadow-none disabled:cursor-not-allowed',
  type: {
    [BUTTON_TYPE.SOLID]: 'shadow-sm border disabled:opacity-90',
    [BUTTON_TYPE.OUTLINE]: 'shadow-sm border disabled:bg-oxford-gray-100',
    [BUTTON_TYPE.TEXT]: ''
  },
  size: {
    [BUTTON_SIZE.SMALL]: 'py-2 px-3 text-xs',
    [BUTTON_SIZE.MEDIUM]: 'h-7 py-2 px-5 text-sm',
    [BUTTON_SIZE.LARGE]: 'py-3 px-6 text-xl'
  },
  optionsSize: {
    [BUTTON_SIZE.SMALL]: 'py-2 px-2 text-xs',
    [BUTTON_SIZE.MEDIUM]: 'px-2 py-2 text-sm',
    [BUTTON_SIZE.LARGE]: 'py-3 px-2 text-xl'
  },
  color: {
    [BUTTON_COLOR.PRIMARY]: {
      [BUTTON_TYPE.SOLID]:
        'bg-primary text-white font-semibold border-primary hover:bg-electric-violet-500 hover:border-electric-violet-500 hover:shadow-electric-violet-400 active:bg-electric-violet-800 disabled:bg-electric-violet-200 disabled:border-electric-violet-200 disabled:hover:bg-electric-violet-200 disabled:active:bg-electric-violet-200',
      [BUTTON_TYPE.OUTLINE]:
        'bg-white text-primary border-primary hover:bg-electric-violet-50 hover:shadow-electric-violet-400 active:bg-electric-violet-100 disabled:border-electric-violet-400 disabled:text-electric-violet-400',
      [BUTTON_TYPE.TEXT]:
        'text-primary hover:text-electric-violet-500 active:text-electric-violet-600 disabled:text-electric-violet-300 disabled:hover:bg-white disabled:active:bg-white'
    },
    [BUTTON_COLOR.DANGER]: {
      [BUTTON_TYPE.SOLID]:
        'bg-watermelon-800 text-white border-watermelon-800 hover:bg-watermelon-500 hover:border-watermelon-500 hover:shadow-sm active:bg-watermelon-900 disabled:bg-watermelon-200 disabled:border-watermelon-200',
      [BUTTON_TYPE.OUTLINE]:
        'bg-white text-watermelon-800 border-watermelon-800 hover:bg-watermelon-50 hover:shadow-sm active:bg-watermelon-100 disabled:border-watermelon-300 disabled:text-watermelon-300',
      [BUTTON_TYPE.TEXT]:
        'text-watermelon-800 hover:text-watermelon-500 active:text-watermelon-600 disabled:text-watermelon-300'
    },
    [BUTTON_COLOR.SUCCESS]: {
      [BUTTON_TYPE.SOLID]:
        'bg-jade-500 text-white border-jade-500 hover:bg-jade-300 hover:border-jade-50 hover:shadow-sm active:bg-jade-300 disabled:bg-jade-200 disabled:border-jade-200',
      [BUTTON_TYPE.OUTLINE]:
        'bg-white text-jade-500 border-jade-500 hover:bg-jade-300 hover:shadow-sm active:bg-jade-300 disabled:border-jade-300 disabled:text-jade-300',
      [BUTTON_TYPE.TEXT]:
        'text-jade-500 hover:text-jade-300 active:text-jade-300 disabled:text-jade-300'
    }
  }
}

const Button: React.FunctionComponent<IButtonProps> = ({
  className,
  disabled,
  loading,
  children,
  leftIcon,
  rightIcon,
  colorClass,
  onClick,
  onClickCapture,
  style,
  htmlType = 'button',
  type = BUTTON_TYPE.SOLID,
  size = BUTTON_SIZE.MEDIUM,
  color = BUTTON_COLOR.PRIMARY,
  optionsButton = false,
  additionalOptions,
  additionalOptionsPopperPlacement = 'bottom-end',
  renderOptionsButtonArrow = true
}) => {
  const renderContent = () => {
    if (leftIcon) {
      return (
        <>
          <i className={`${leftIcon} mr-2 pointer-events-none`}></i>
          {children}
        </>
      )
    }

    if (rightIcon) {
      return (
        <>
          {children}
          <i className={`${rightIcon} ml-2 pointer-events-none`}></i>
        </>
      )
    }

    return children
  }

  const btnColor = colorClass ? colorClass : theme.color[color][type]

  const buttonClasses = classnames([
    theme.shared,
    theme.type[type],
    theme.size[size],
    btnColor,
    className
  ])

  const buttonContent = (
    <>
      {loading ? (
        <Spinner className={type === BUTTON_TYPE.SOLID ? 'text-white' : ''} />
      ) : (
        renderContent()
      )}
    </>
  )

  const renderOptionsContent = (close: Function) => (
    <div className="bg-white border border-oxford-gray-200 rounded-md shadow-lg">
      {additionalOptions?.map((option, index) => (
        <button
          key={index}
          disabled={option.disabled}
          type="button"
          className={classnames(
            'w-full flex items-center justify-start py-3 px-4 text-sm transition-colors',
            {
              'hover:bg-oxford-gray-100': !option.disabled,
              'text-oxford-gray-400': option.disabled
            }
          )}
          onClick={() => {
            option.onClick()
            close()
          }}
        >
          {option.icon && <i className={classnames(['mr-2', option.icon])}></i>}
          <span>{option.name}</span>
        </button>
      ))}
    </div>
  )

  if (!additionalOptions || !additionalOptions.length) {
    return (
      <button
        type={htmlType}
        style={style}
        onClick={onClick}
        onClickCapture={onClickCapture}
        disabled={loading || disabled}
        className={buttonClasses}
      >
        {buttonContent}
      </button>
    )
  }

  if (optionsButton) {
    return (
      <Popover
        portalMode
        panelClassName="z-50"
        button={({ open }) => (
          <button
            type={htmlType}
            style={style}
            disabled={loading || disabled}
            className={buttonClasses}
          >
            {buttonContent}

            {renderOptionsButtonArrow && (
              <i
                className={classnames(
                  'far fa-chevron-down pointer-events-none ml-2',
                  {
                    'rotate-180': open
                  }
                )}
              ></i>
            )}
          </button>
        )}
        content={({ close }) => renderOptionsContent(close)}
        popperPlacement={additionalOptionsPopperPlacement}
      />
    )
  }

  return (
    <div className="flex">
      <button
        type={htmlType}
        style={style}
        onClick={onClick}
        onClickCapture={onClickCapture}
        disabled={loading || disabled}
        className={classnames([buttonClasses, 'rounded-r-none border-r-0'])}
      >
        {buttonContent}
      </button>
      <Popover
        portalMode
        containerClassName="flex-1 flex"
        buttonClassName="flex-1 flex"
        button={({ open }) => (
          <button
            type="button"
            disabled={loading || disabled}
            className={classnames([
              theme.shared,
              theme.type[type],
              theme.optionsSize[size],
              'flex-1 flex items-center justify-center rounded-md border border-oxford-gray-200 rounded-l-none',
              type === BUTTON_TYPE.SOLID && 'border-l-white/20',
              type === BUTTON_TYPE.OUTLINE && 'border-l-current/20',
              btnColor
            ])}
          >
            <i
              className={classnames('far fa-chevron-down pointer-events-none', {
                'rotate-180': open
              })}
            ></i>
          </button>
        )}
        content={({ close }) => renderOptionsContent(close)}
        popperPlacement={additionalOptionsPopperPlacement}
      />
    </div>
  )
}

export default Button
